import React from 'react';
import { Facebook, ExternalLink, ThumbsUp, MessageCircle, Share2, Users, Heart } from 'lucide-react';

export const SocialFeed = () => {
  const facebookPageUrl = 'https://www.facebook.com/ThreeThirdsSociety/';
  const facebookPageName = '3/3 Society';

  return (
    <section id="social" className="py-20 bg-gradient-to-br from-gray-50 to-emerald-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
            <Facebook className="w-4 h-4 mr-2" />
            Stay Connected
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Follow Us on <span className="text-blue-600">Facebook</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Stay updated with our latest news, events, and community activities
          </p>
        </div>

        {/* Facebook Feed Container */}
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
          {/* Facebook Page Card */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden w-full max-w-md">
            {/* Cover Image Gradient */}
            <div className="h-24 bg-gradient-to-r from-blue-600 to-blue-700"></div>
            
            {/* Page Info */}
            <div className="px-6 pb-6">
              {/* Profile Picture */}
              <div className="flex justify-center -mt-12 mb-4">
                <div className="w-24 h-24 bg-white rounded-full p-1 shadow-lg">
                  <div className="w-full h-full bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-2xl font-bold">3/3</span>
                  </div>
                </div>
              </div>
              
              {/* Page Name & Stats */}
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-1">{facebookPageName}</h3>
                <p className="text-gray-500 text-sm mb-3">@ThreeThirdsSociety</p>
                <div className="flex justify-center gap-6 text-sm">
                  <div className="flex items-center text-gray-600">
                    <Users className="w-4 h-4 mr-1" />
                    <span>481+ followers</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Heart className="w-4 h-4 mr-1" />
                    <span>Community</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <a
                  href={facebookPageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors duration-200"
                >
                  <Facebook className="w-5 h-5 mr-2" />
                  Follow Us on Facebook
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
                <a
                  href={facebookPageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors duration-200"
                >
                  View Latest Posts
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </div>
            </div>
          </div>

          {/* Call to Action Side Panel */}
          <div className="max-w-sm space-y-6">
            {/* Stats Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Facebook className="w-6 h-6 text-blue-600 mr-2" />
                Join Our Community
              </h3>
              <p className="text-gray-600 mb-6">
                Connect with us on Facebook to get the latest updates on our social economy initiatives, 
                upcoming events, and success stories from our community.
              </p>
              
              {/* Engagement Features */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-3 bg-blue-50 rounded-xl">
                  <ThumbsUp className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                  <span className="text-xs text-gray-600">Like</span>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-xl">
                  <MessageCircle className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                  <span className="text-xs text-gray-600">Comment</span>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-xl">
                  <Share2 className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                  <span className="text-xs text-gray-600">Share</span>
                </div>
              </div>
            </div>

            {/* Recent Activity Highlights */}
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl shadow-lg p-6 text-white">
              <h4 className="font-bold mb-3">What We Share</h4>
              <ul className="space-y-2 text-emerald-100">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                  Project updates & milestones
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                  Workshop announcements
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                  Community success stories
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                  Funding opportunities
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialFeed;
